import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import moment from "moment";
import { ColDef } from "ag-grid-community";
import { Copy, Download, Search } from "lucide-react";
import { toast } from "react-toastify";

import PaginationLayout from "../components/PaginationLayout";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";
import DownloadfileModal from "../components/common/downloadfileModal";
import henceofrthEnums from "../utils/henceofrthEnums";
import {
  downloadExcelFile,
  ExportColumn,
} from "../components/common/ExportButton";

import AgGridTable from "../components/ui/AgGridTable";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const transactionExportColumns: ExportColumn[] = [
  { header: "Sr No.", key: "sr_no", type: "number", width: 8 },
  { header: "Transaction ID", key: "order_id", type: "text", width: 18 },
  { header: "Passenger", key: "customer_name", type: "text", width: 18 },
  { header: "Driver", key: "driver_name", type: "text", width: 18 },
  {
    header: "Amount",
    key: "amount",
    type: "currency",
    isAmount: true,
    width: 14,
  },
  { header: "Payment ID", key: "paymentIntentId", type: "text", width: 28 },
  { header: "Date", key: "created_at", type: "date", width: 18 },
];

interface translist {
  order_id: string;
  customer_name: string;
  driver_name: string;
}
interface transaction {
  id: string;
  booking_detail: translist;
  amount: number;
  currency?: any;
  paymentIntentId?: string;
  created_at?: string;
}

const ManageTransaction = () => {
  const location = useLocation();
  const limit = 10;
  const { authState, handlePagination, handleFilter, handleError } =
    useContext(GlobalContext);
  const params: any = useParams();
  const newParam = new URLSearchParams(location.search);
  const [loading, setLoading] = useState(false);
  const [pageData, setPageData] = useState<Array<transaction>>([]);
  const [totalCount, setTotalCount] = useState(0);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const exportData = async (start_date: number, end_date: number) => {
    try {
      let apiRes = await henceforthApi.Transaction.export(
        moment(start_date).format("DD/MM/YYYY"),
        moment(end_date).format("DD/MM/YYYY")
      );
      let data = apiRes?.data || [];

      const rows = data.map((res: any, index: number) => ({
        sr_no: index + 1,
        order_id: res?.order_id || "N/A",
        customer_name: res?.customer_name || "N/A",
        driver_name: res?.driver_name || "N/A",
        amount: Number(res?.amount || 0),
        paymentIntentId: res?.paymentIntentId || "N/A",
        created_at: res?.created_at ? Number(res.created_at) : "",
      }));

      await downloadExcelFile({
        fileName: `transactions_${moment().format("YYYY-MM-DD")}`,
        sheetName: "Transactions",
        columns: transactionExportColumns,
        data: rows,
      });

      let closeModal = document.getElementById("closeModal");
      if (closeModal) {
        closeModal.click();
      }
    } catch (error: any) {
      console.error("Transactions export error:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        let apiRes = await henceforthApi.ManageTransaction.getPagnation(
          limit,
          Number(params.page) - 1,
          newParam.toString()
        );
        setTotalCount(apiRes.data.total_count);
        setPageData(apiRes.data.data || []);
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        setPageData([]);
        setTotalCount(0);
        handleError(error);
      }
    };
    init();
  }, [params.page, newParam.get("search")]);

  const copyText = (txt: string, label: string) => {
    if (!txt) return;
    navigator.clipboard
      .writeText(txt)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error("Copy failed"));
  };

  const formatCurrency = (amount: number, currency: any) => {
    const formatted = Number(amount || 0).toFixed(2);
    if (currency === henceofrthEnums.CurrencyType.NGN) return `₦ ${formatted}`;
    if (currency === henceofrthEnums.CurrencyType.INR) return `₹ ${formatted}`;
    return `$ ${formatted}`;
  };

  // AG Grid Column Definitions
  const columnDefs = useMemo<ColDef[]>(() => {
    const pageNum = Number(params.page) || 1;
    return [
      {
        headerName: "Sr No.",
        minWidth: 70,
        maxWidth: 80,
        width: 75,
        valueGetter: (p) => {
          return (pageNum - 1) * limit + (p.node?.rowIndex ?? 0) + 1;
        },
        cellClass: "font-medium text-slate-500",
      },
      {
        headerName: "Transaction Id",
        field: "booking_detail.order_id",
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => {
          const val = p.value || "Not available";
          return (
            <div
              onClick={() => copyText(val, "Transaction ID")}
              title="Click to copy ID"
              className="group flex items-center gap-1.5 cursor-pointer text-slate-800 hover:text-[#006747] font-semibold text-sm"
            >
              <span>{val}</span>
              <Copy className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#006747] opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          );
        },
      },
      {
        headerName: "Passenger",
        field: "booking_detail.customer_name",
        minWidth: 140,
        flex: 1,
        cellRenderer: (p: any) => {
          return (
            <span className="text-slate-800 text-sm font-medium">
              {p.value || "Not available"}
            </span>
          );
        },
      },
      {
        headerName: "Driver",
        field: "booking_detail.driver_name",
        minWidth: 140,
        flex: 1,
        cellRenderer: (p: any) => {
          return (
            <span className="text-slate-700 text-sm font-medium">
              {p.value || "Not available"}
            </span>
          );
        },
      },
      {
        headerName: "Amount",
        field: "amount",
        minWidth: 100,
        width: 110,
        cellRenderer: (p: any) => {
          return (
            <span className="font-bold text-slate-900 text-sm">
              {formatCurrency(p.value, p.data?.currency)}
            </span>
          );
        },
      },
      {
        headerName: "Payment Id",
        field: "paymentIntentId",
        minWidth: 160,
        flex: 1.2,
        cellRenderer: (p: any) => {
          const val = p.value || "Not available";
          return (
            <span className="text-slate-500 text-sm truncate" title={val}>
              {val}
            </span>
          );
        },
      },
    ];
  }, [params.page]);

  return (
    <>
      {/* Compact Single Header Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        {/* Left: Search Input */}
        <div className="relative w-64 sm:w-72">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            name="search"
            defaultValue={newParam.get("search") || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleFilter(e.target.name, e.target.value);
            }}
            placeholder="Search passenger, driver or order ID..."
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Export Button */}
          <Button
            variant="default"
            size="sm"
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
            className="h-9 px-4 text-sm gap-1.5 shadow-xs"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* AG Grid Table */}
      <AgGridTable
        rowData={pageData}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No transactions found for this search"
      />

      <div className="mt-3">
        <PaginationLayout
          count={totalCount}
          data={pageData}
          page={Number(params.page)}
          limit={Number(limit)}
          loading={loading}
          onPageChange={(val) => handlePagination(val)}
        />
      </div>

      <DownloadfileModal data={exportData} />
    </>
  );
};

export default ManageTransaction;